import client from './client';

/**
 * Wallcraft → Spectrum pre-time-card export.
 *
 * Three calls against /admin/wallcraft/spectrum, all scoped to ONE supervisor's crew for
 * ONE pay period (the API has no bucket-wide equivalent — `supervisorID` is required):
 *   GET  …/preview   → the rows we would send, plus who has already been pushed
 *   GET  …/file      → the same rows as an xlsx (nothing leaves our system)
 *   POST …/push      → sends them to Spectrum's Data Exchange PreTimeCardBatch service
 *
 * Replaces the Pick-Up Sheet workbook the payroll clerk used to key in by hand.
 */

/** One field Spectrum (or our own pre-flight validator) refused on one row. */
export interface SpectrumRowRejection {
    /** YYYY-MM-DD. */
    workDate: string;
    payType: string | null;
    hours: string;
    /** The offending column, e.g. "phaseCode". */
    field: string;
    /** What is wrong, e.g. "Phase may not contain dashes". */
    code: string;
    /** The value that was rejected. */
    value: string;
}

/** The 422 payload groups every rejection under the employee it belongs to. */
export interface EmployeeSpectrumIssues {
    /** 0 when the API could not resolve the rejected line back to an employee. */
    employeeID: number;
    altEmployeeID: string;
    /** The API's key is `issues` (src/wallcraft/spectrumExportTypes.ts, EmployeeSpectrumIssues). */
    issues: SpectrumRowRejection[];
}

/**
 * Thrown by all three calls so the modal can render the per-employee breakdown.
 * `status` is carried because 503 (push not configured) and 404 (no payroll-approved
 * timesheets) get their own copy — the server's own wording is not user-facing.
 */
export class SpectrumExportError extends Error {
    rejections?: EmployeeSpectrumIssues[];
    status?: number;
    constructor(message: string, rejections?: EmployeeSpectrumIssues[], status?: number) {
        super(message);
        this.name = 'SpectrumExportError';
        this.rejections = rejections;
        this.status = status;
    }
}

/** What kind of line this is — the preview subtotals bonus/meal/phone rows separately. */
export type SpectrumLineKind = 'work' | 'sick' | 'bonus' | 'meal' | 'phone';

/** One pre-time-card line, exactly as it would reach Spectrum. */
export interface SpectrumPreviewRow {
    employeeID: number;
    altEmployeeID: string;
    name: string;
    /** YYYY-MM-DD. */
    workDate: string;
    /** R / O / D / S, or the env-configured bonus / meal-premium code. */
    payType: string;
    lineKind: SpectrumLineKind;
    /** Null on bonus and phone lines — dollars only, no time. */
    hours: number | null;
    jobNumber: string;
    phaseCode: string;
    department: string;
    payRate: number;
    /**
     * True when `phaseCode` is a purchase-order number, which does not exist on the job
     * until the push creates it (AddPhase). Absent on API builds older than that change —
     * read it as "no PO phases".
     */
    isPoPhase?: boolean;
}

/**
 * One AddPhase call the push makes BEFORE any pre-time card is sent. A purchase-order
 * phase gets two of them, cost type "1" (labor) then "2" (burden); operation phases are
 * Spectrum masters and never appear here.
 */
export interface SpectrumPreviewPhaseRow {
    coCode: string;
    jobNumber: string;
    /** The PO number, e.g. "99999". */
    phaseCode: string;
    /** Job name + crew code, the same text the Phase Import sheet carries. */
    description: string;
    /** "1" or "2". */
    costType: string;
}

export interface SpectrumPreview {
    /** The Spectrum batch this crew lands in — the supervisor's bare crew code. */
    batchCode: string;
    crewCode: string;
    supervisor: { employeeID: number; name: string };
    payPeriod: { payPeriodID: number; weekNumber: number; startDate: string; endDate: string };
    rowCount: number;
    employeeCount: number;
    /** Hours keyed by pay type across the whole crew (unfiltered by selection). */
    hoursByPayType: Record<string, number>;
    /** Employees with a Spectrum batch line already recorded for this pay period. */
    alreadyPushedEmployeeIDs: number[];
    rows: SpectrumPreviewRow[];
    /**
     * Every PO phase the push would create for the whole crew (unfiltered by selection —
     * the modal narrows it to the checked employees). Absent on older API builds.
     */
    phaseRows?: SpectrumPreviewPhaseRow[];
    /**
     * This crew's employees whose live timecard no longer matches what was pushed to
     * Spectrum (see getSpectrumChanges). Absent on older API builds — read it as [].
     */
    changedSincePush?: SpectrumDriftEmployee[];
}

/** Per-employee totals, computed the same way on the pushed and the live side. */
export interface SpectrumDriftTotals {
    reg: number;
    ot: number;
    dt: number;
    sick: number;
    totalHours: number;
    gross: number;
}

/** One (date, pay type, kind, job, phase, dept) key whose hours or dollars differ. */
export interface SpectrumDriftLineChange {
    /** MM/DD/YYYY (the key the API diffs on). */
    workDate: string;
    payType: string;
    lineKind: string;
    jobNumber: string;
    phaseCode: string;
    department: string;
    /** Null when the key is absent on that side (or a dollars-only line). */
    pushedHours: number | null;
    currentHours: number | null;
    pushedAmount: number;
    currentAmount: number;
}

/** One edit made after the push — detail only; the diff decides the flag. */
export interface SpectrumDriftEdit {
    /** ISO timestamp (UTC). */
    at: string;
    /** e.g. "PAYROLL", "SUPERVISOR", "APPROVAL". */
    source: string;
    who: string | null;
    entity: string;
    field: string | null;
    oldValue: string | null;
    newValue: string | null;
}

/** An employee whose live timecard no longer matches their latest Spectrum push. */
export interface SpectrumDriftEmployee {
    employeeID: number;
    altEmployeeID: string;
    name: string;
    /** The crew's supervisor — matches the board's crew-group employeeID. */
    supervisorID: number;
    crewCode: string;
    /** ISO timestamp of the employee's latest push. */
    pushedAt: string;
    approvalStatus: number | null;
    /** False when the timesheet dropped out of Payroll Approved after the push. */
    stillApproved: boolean;
    pushedTotals: SpectrumDriftTotals;
    currentTotals: SpectrumDriftTotals;
    lineChanges: SpectrumDriftLineChange[];
    edits: SpectrumDriftEdit[];
}

export interface SpectrumChangesResponse {
    payPeriodID: number;
    checkedAt: string;
    /** Only the flagged employees — empty when nothing drifted. */
    employees: SpectrumDriftEmployee[];
}

export interface SpectrumPushResult {
    batchCode: string;
    rowCount: number;
    employeeCount: number;
    /** The human summary — rows, employees and, when there were any, the PO phase counts. */
    message: string;
    /** PO phases this push created on the job, and how many Spectrum already had. */
    phasesCreated?: number;
    phasesExisting?: number;
    /** The GUID the pre-time card batch was sent under. */
    spectrumBatchGuid?: string;
    /** Non-fatal notes — an already-existing phase, a Spectrum W code. */
    warnings?: string[];
}

/**
 * The API aborts with 422 { errorMessage, rejections } both for its own pre-flight
 * validation and for rows Spectrum itself refuses — same shape, same renderer, and in
 * both cases NOTHING was saved. With responseType "blob" that body arrives as a Blob,
 * so it has to be read back off the wire before it can reach the UI. Modeled on
 * decodeSageError in src/api/2020ph.ts — Sage has the same 200-on-error trap.
 */
async function decodeSpectrumError(
    error: unknown,
    opts?: { keepServer404?: boolean }
): Promise<SpectrumExportError> {
    const response = (error as { response?: { data?: unknown; status?: number } })?.response;
    const status = response?.status;
    let body: unknown = response?.data;
    if (body instanceof Blob) {
        const text = await body.text().catch(() => '');
        try {
            body = JSON.parse(text);
        } catch {
            body = text;
        }
    }

    // Status-specific copy wins over whatever the server said — these two are operational
    // states the payroll clerk can act on, not export failures.
    if (status === 503) {
        return new SpectrumExportError(
            'Spectrum push is not configured on the API (SPECTRUM_SDX_* settings).',
            undefined,
            503
        );
    }
    if (status === 404 && !opts?.keepServer404) {
        return new SpectrumExportError('No payroll-approved timesheets for this crew.', undefined, 404);
    }

    if (typeof body === 'string' && body.trim()) {
        return new SpectrumExportError(body.trim(), undefined, status);
    }
    const obj = body as {
        errorMessage?: string;
        message?: string;
        rejections?: EmployeeSpectrumIssues[];
    } | null;
    const message =
        obj?.errorMessage ||
        obj?.message ||
        (error instanceof Error ? error.message : 'The Spectrum export failed.');
    return new SpectrumExportError(
        message,
        Array.isArray(obj?.rejections) ? obj?.rejections : undefined,
        status
    );
}

/**
 * employeeIDs travels as a comma-separated string — a raw array would serialize as
 * repeated params, which these endpoints don't accept (same rule as generateSageExport).
 */
function employeeIDsParam(employeeIDs?: number[]): Record<string, string> {
    return employeeIDs?.length ? { employeeIDs: employeeIDs.join(',') } : {};
}

/** The rows we would send for this crew. Read-only — nothing leaves our system. */
export async function previewSpectrumCrew(
    supervisorID: number,
    payPeriodID: number,
    employeeIDs?: number[]
): Promise<SpectrumPreview> {
    try {
        const res = await client.get('/admin/wallcraft/spectrum/preview', {
            params: { supervisorID, payPeriodID, ...employeeIDsParam(employeeIDs) },
        });
        return res.data as SpectrumPreview;
    } catch (error) {
        throw await decodeSpectrumError(error);
    }
}

/**
 * The same rows as a human-inspectable xlsx. The endpoint is synchronous (no job/poll)
 * and names the file itself, so the Content-Disposition filename wins over the fallback.
 */
export async function downloadSpectrumFile(
    supervisorID: number,
    payPeriodID: number,
    fallbackFileName: string,
    employeeIDs?: number[]
): Promise<void> {
    let response;
    try {
        response = await client.get('/admin/wallcraft/spectrum/file', {
            params: { supervisorID, payPeriodID, ...employeeIDsParam(employeeIDs) },
            responseType: 'blob',
        });
    } catch (error) {
        throw await decodeSpectrumError(error);
    }

    // The server's filename contains no spaces today, but the helper honors the quoted form.
    saveBlobResponse(response, fallbackFileName);
}

/**
 * Send the crew to Spectrum. `force` re-sends employees already recorded as pushed —
 * Spectrum has no delete and the batch code is the bare crew code, so a forced re-push
 * APPENDS to the same batch and the clerk has to clear the earlier un-approved rows first.
 */
export async function pushSpectrumCrew(
    supervisorID: number,
    payPeriodID: number,
    employeeIDs: number[],
    force: boolean
): Promise<SpectrumPushResult> {
    try {
        const res = await client.post(
            '/admin/wallcraft/spectrum/push',
            // A JSON array here, unlike the GETs' comma-separated query string.
            { ...(employeeIDs.length ? { employeeIDs } : {}), ...(force ? { force: true } : {}) },
            { params: { supervisorID, payPeriodID } }
        );
        return res.data as SpectrumPushResult;
    } catch (error) {
        throw await decodeSpectrumError(error);
    }
}

/** Pull the Content-Disposition filename off a blob response and save it. */
function saveBlobResponse(
    response: { data: unknown; headers: unknown },
    fallbackFileName: string
): void {
    const disposition = (response.headers as Record<string, string | undefined>)['content-disposition'];
    const match = disposition?.match(/filename[^;=\n]*=(['"]?)([^'";\n]+)\1/);
    const fileName = match ? match[2].trim() : fallbackFileName;

    const url = URL.createObjectURL(response.data as Blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }, 100);
}

/**
 * The week's Payroll Summary workbook — every crew, built from the lines AS PUSHED to
 * Spectrum (not recomputed from the timecards). Synchronous, like the crew file. A 404
 * carries the server's own wording ("No timesheets have been exported to Spectrum for
 * Week N yet."), so it is kept rather than replaced with the crew-level copy.
 */
export async function downloadSpectrumSummaryFile(
    payPeriodID: number,
    fallbackFileName: string
): Promise<void> {
    let response;
    try {
        response = await client.get('/admin/wallcraft/spectrum/summary/file', {
            params: { payPeriodID },
            responseType: 'blob',
        });
    } catch (error) {
        const decoded = await decodeSpectrumError(error, { keepServer404: true });
        if (decoded.status === 404 && !decoded.message) {
            decoded.message = 'Nothing has been exported to Spectrum for this week yet.';
        }
        throw decoded;
    }
    saveBlobResponse(response, fallbackFileName);
}

/**
 * The week's Payroll Detail workbook — one tab per crew (employee totals + line detail),
 * built from the lines AS PUSHED to Spectrum. Same shape and 404 handling as the summary.
 */
export async function downloadSpectrumDetailFile(
    payPeriodID: number,
    fallbackFileName: string
): Promise<void> {
    let response;
    try {
        response = await client.get('/admin/wallcraft/spectrum/detail/file', {
            params: { payPeriodID },
            responseType: 'blob',
        });
    } catch (error) {
        const decoded = await decodeSpectrumError(error, { keepServer404: true });
        if (decoded.status === 404 && !decoded.message) {
            decoded.message = 'Nothing has been exported to Spectrum for this week yet.';
        }
        throw decoded;
    }
    saveBlobResponse(response, fallbackFileName);
}

/** One employee pushed to Spectrum this pay period (their latest push). */
export interface SpectrumExportedInfo {
    employeeID: number;
    altEmployeeID: string;
    name: string;
    /** Crew of the latest push — matches the board's crew-group employeeID. */
    supervisorID: number;
    crewCode: string;
    /** ISO timestamp of the latest push. */
    pushedAt: string;
    /** >1 = pushed more than once. */
    pushCount: number;
    lineCount: number;
    hours: number;
    stillApproved: boolean;
}

export interface SpectrumExportStatus {
    payPeriodID: number;
    employees: SpectrumExportedInfo[];
}

/**
 * Who has been pushed to Spectrum for the week — the board's Exported / Not exported chips,
 * tile counts and filter. Pay-period wide, read from the push record. Read-only.
 */
export async function getSpectrumExportStatus(payPeriodID: number): Promise<SpectrumExportStatus> {
    try {
        const res = await client.get('/admin/wallcraft/spectrum/status', { params: { payPeriodID } });
        const data = res.data as Partial<SpectrumExportStatus> | null;
        return {
            payPeriodID: data?.payPeriodID ?? payPeriodID,
            employees: Array.isArray(data?.employees) ? data!.employees : [],
        };
    } catch (error) {
        throw await decodeSpectrumError(error, { keepServer404: true });
    }
}

/** "Pushed 10/12 1:16p · 11 lines · 40.00 hrs · pushed 2 times" (chip tooltips). */
export function spectrumExportFacts(x: SpectrumExportedInfo): string {
    const d = new Date(x.pushedAt);
    let when = '';
    if (!Number.isNaN(d.getTime())) {
        const h = d.getHours();
        const mm = String(d.getMinutes()).padStart(2, '0');
        when = ` ${d.getMonth() + 1}/${d.getDate()} ${h % 12 === 0 ? 12 : h % 12}:${mm}${h < 12 ? 'a' : 'p'}`;
    }
    return [
        `Pushed to Spectrum${when}${x.crewCode ? ` (crew ${x.crewCode})` : ''}`,
        `${x.lineCount} line${x.lineCount === 1 ? '' : 's'}`,
        `${x.hours.toFixed(2)} hrs`,
        ...(x.pushCount > 1 ? [`pushed ${x.pushCount} times`] : []),
    ].join(' · ');
}

/**
 * Employees whose live timecard no longer matches their latest Spectrum push for the
 * week (a live diff; the edit log only supplies who/what/when). Read-only.
 */
export async function getSpectrumChanges(payPeriodID: number): Promise<SpectrumChangesResponse> {
    try {
        const res = await client.get('/admin/wallcraft/spectrum/changes', { params: { payPeriodID } });
        const data = res.data as Partial<SpectrumChangesResponse> | null;
        return {
            payPeriodID: data?.payPeriodID ?? payPeriodID,
            checkedAt: data?.checkedAt ?? new Date().toISOString(),
            employees: Array.isArray(data?.employees) ? data!.employees : [],
        };
    } catch (error) {
        throw await decodeSpectrumError(error, { keepServer404: true });
    }
}

/* ── Changed-since-push formatting (shared by the board and the export modal) ── */

const driftMoney = (n: number) =>
    n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const driftHours = (n: number | null) => (n == null ? '—' : n.toFixed(2));

/** ISO timestamp → "9/19 3:12 PM" in the viewer's local time. */
export function formatDriftWhen(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    return `${d.getMonth() + 1}/${d.getDate()} ${time}`;
}

/** Edit-log values: a timestamp reads as a clock time, anything else as-is. */
function formatDriftValue(v: string | null): string {
    if (v == null || v === '') return '—';
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(v)) {
        const d = new Date(v);
        if (!Number.isNaN(d.getTime())) {
            return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
        }
    }
    return v;
}

/** "9/19 3:12 PM · PAYROLL (Jane D.) · TimeEntry InDateTime 7:00 AM → 6:30 AM" */
export function formatDriftEdit(e: SpectrumDriftEdit): string {
    const who = e.who ? `${e.source} (${e.who})` : e.source;
    const what = [e.entity, e.field].filter(Boolean).join(' ');
    const change = e.oldValue != null || e.newValue != null
        ? ` ${formatDriftValue(e.oldValue)} → ${formatDriftValue(e.newValue)}`
        : '';
    return `${formatDriftWhen(e.at)} · ${who} · ${what}${change}`;
}

/** "REG 40.00 · OT 1.00 · DT 0.00 · Sick 0.00 · 41.00 h · $1,030.00" */
export function formatDriftTotals(t: SpectrumDriftTotals): string {
    return `REG ${t.reg.toFixed(2)} · OT ${t.ot.toFixed(2)} · DT ${t.dt.toFixed(2)} · Sick ${t.sick.toFixed(2)}`
        + ` · ${t.totalHours.toFixed(2)} h · ${driftMoney(t.gross)}`;
}

/** "41.00 h / $1,030.00 → 42.00 h / $1,060.00" */
export function formatDriftTotalsShift(d: SpectrumDriftEmployee): string {
    const p = d.pushedTotals;
    const c = d.currentTotals;
    return `${p.totalHours.toFixed(2)} h / ${driftMoney(p.gross)} → ${c.totalHours.toFixed(2)} h / ${driftMoney(c.gross)}`;
}

/** "Tue 9/8 · 1204 (410) · OT 4.00 → 5.00 h" — or dollars for bonus/phone lines. */
export function formatDriftLineChange(c: SpectrumDriftLineChange): string {
    let date = c.workDate;
    const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(c.workDate) ?? null;
    const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(c.workDate);
    const [y, mo, da] = m
        ? [Number(m[3]), Number(m[1]), Number(m[2])]
        : iso ? [Number(iso[1]), Number(iso[2]), Number(iso[3])] : [0, 0, 0];
    if (y) {
        const d = new Date(Date.UTC(y, mo - 1, da));
        date = `${d.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' })} ${mo}/${da}`;
    }
    const where = `${c.jobNumber || c.department || '—'}${c.phaseCode ? ` (${c.phaseCode})` : ''}`;
    const kind = c.lineKind === 'work' || c.lineKind === 'sick' ? c.payType : c.lineKind.toUpperCase();
    const value = c.pushedHours != null || c.currentHours != null
        ? `${driftHours(c.pushedHours)} → ${driftHours(c.currentHours)} h`
        : `${driftMoney(c.pushedAmount)} → ${driftMoney(c.currentAmount)}`;
    return `${date} · ${where} · ${kind} ${value}`;
}

/** Newest edit first. */
export function sortedDriftEdits(d: SpectrumDriftEmployee): SpectrumDriftEdit[] {
    return [...(d.edits ?? [])].sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0));
}

/** Plain-text tooltip for the board's "Changed since Spectrum push" chip. */
export function driftTooltip(d: SpectrumDriftEmployee): string {
    const lines = [
        `Changed since Spectrum push (pushed ${formatDriftWhen(d.pushedAt)})`,
        `Pushed:  ${formatDriftTotals(d.pushedTotals)}`,
        `Current: ${formatDriftTotals(d.currentTotals)}`,
    ];
    if (!d.stillApproved) lines.push('No longer Payroll Approved');
    const edits = sortedDriftEdits(d);
    if (edits.length) {
        lines.push('', 'Edits since the push:');
        edits.slice(0, 8).forEach((e) => lines.push(formatDriftEdit(e)));
        if (edits.length > 8) lines.push(`…and ${edits.length - 8} more`);
    }
    return lines.join('\n');
}
